/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../Containers/ArgosFlexbox"
import ArgosButton, {ArgosLoadingButton} from "../Buttons/ArgosButton"
import {KeyboardArrowLeft, KeyboardArrowRight} from "@mui/icons-material"
import Step from "@mui/material/Step"
import StepLabel from "@mui/material/StepLabel"
import Stepper from "@mui/material/Stepper"
import {useState} from "react"
import { MultiPageWizardProps } from "./MultiPageWizard.types"

export const MultiPageWizard = (props: MultiPageWizardProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const finishButtonText = props.finishButtonText || 'Finish'
  
  return (
    <div style={{height: '100%', width: '100%', overflow: 'auto'}}>
      {props.children?.map((child: any, index: number) => {
        const shouldDisplay = currentPage === index
        return (
          <ArgosFlexbox key={index} vertical
                        style={{display: shouldDisplay ? '' : 'none', width: '100%', height: '100%', overflow: 'auto'}}>
            {props?.steps && <Stepper activeStep={index} style={{padding: '20px'}}>
              {props?.steps?.map((label, index) => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: {
                  optional?: React.ReactNode;
                } = {};
                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel {...labelProps}>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>}
            <div style={{overflow: 'auto', flex: 1}}>
              {child}
            </div>
            <ArgosFlexbox style={{display: 'flex', flexDirection: 'row', padding: '10px'}}>
              <ArgosButton
                id={`back-button-${index}`}
                data-testid={`back-button-${index}`}
                variant="text"
                size="large"
                fontSize={16}
                onClick={() => {
                  setCurrentPage(prevState => prevState > 0 ? prevState - 1 : prevState)
                }}
                disabled={currentPage === 0}
              >
                <KeyboardArrowLeft />
                Back
              </ArgosButton>
              <ArgosButton
                id={`cancel-button-${index}`}
                data-testid={`cancel-button-${index}`}
                variant="text"
                size="large"
                fontSize={16}
                onClick={props.onClickClose}
              >
                Cancel
              </ArgosButton>
              <ArgosFlexbox style={{flex: '1 1 auto'}} />
              {
                index !== props.children?.length - 1 ?
                  <ArgosLoadingButton
                    id={`next-button-${index}`}
                    data-testid={`next-button-${index}`}
                    variant="text"
                    size="large"
                    fontSize={16}
                    disabled={props.isNextDisabled?.[index] || false}
                    onClick={() => {
                      setCurrentPage(prevState => prevState < props.children?.length - 1 ? prevState + 1 : prevState)
                    }}
                  >
                    Next
                    <KeyboardArrowRight />
                  </ArgosLoadingButton>
                  :
                  <ArgosLoadingButton
                    id="finish-button"
                    data-testid="finish-button"
                    variant="text"
                    size="large"
                    fontSize={16}
                    disabled={props.isFinishDisabled || props.isNextDisabled?.[index]}
                    isLoading={props.isFinishLoading}
                    data-loading={props.isFinishLoading}
                    onClick={() => {
                      if (props.onFinish) {
                        props.onFinish()
                      }
                    }}
                  >
                    {finishButtonText}
                  </ArgosLoadingButton>
              }
            </ArgosFlexbox>
          </ArgosFlexbox>
        )
      })}
    </div>
  )
}
